import React from 'react'
import { string, array, bool } from 'prop-types'
import styled from 'styled-components'
import { stripUnit } from 'polished'

import Icon from 'components/Icon'
import Tooltip from 'components/Tooltip'
import { white } from 'styles/colors'
import * as spacing from 'styles/spacing'
import { borderRadius } from 'styles/global'
import { H4, fontWeights, fontSizes } from 'styles/typography'
import { media, mediaDown } from 'styles/media'

import Checkmark from 'icons/checkmark.svg'

const Container = styled.div`
  width: 100%;

  ${mediaDown.medium`
    margin: 0 auto;
    max-width: 460px;
  `};

  ${media.medium`
    display: flex;
    flex-direction: column;
  `};
`

const Inner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: ${spacing.xLarge} ${spacing.large};
  background-color: #15171e;
  border-radius: ${stripUnit(borderRadius) * 3 + 'px'};
  flex: 1;

  &:last-child {
    flex-grow: 0;
  }

  ${media.large`
    padding: ${spacing.xxLarge} ${spacing.xLarge};
    padding-bottom: 64px;
  `};

  ${media.xLarge`
    padding: ${spacing.xxxLarge} ${spacing.xxLarge};
    padding-bottom: 72px;
  `};
`

const Name = styled(H4)`
  color: ${props => props.theme};
  border: 1px solid ${props => props.theme};
  border-radius: ${borderRadius};
  font-weight: ${fontWeights.medium};
  width: 112px;
  padding: ${spacing.small} ${spacing.medium};
  margin: 0 auto ${spacing.xLarge};

  ${media.medium`
    width: 140px;
  `};

  ${media.large`
    width: 160px;
  `};

  ${media.large`
    margin-bottom: ${spacing.xxLarge};
  `};

  ${media.xLarge`
    margin-bottom: ${spacing.xxxLarge};
  `};
`

const Content = styled.div`
  display: block;

  ${mediaDown.medium`
    margin: 0 auto;
    max-width: 280px;
  `};

  ${media.large`
    width: 240px;
  `};
`

const Benefit = styled.div`
  display: flex;
  align-items: flex-start;
  text-align: left;
  color: ${white};
  font-size: ${fontSizes.small};

  & + & {
    margin-top: ${spacing.large};

    ${media.large`
      margin-top: ${spacing.xLarge};
    `};
  }
`

const IconWrap = styled.div`
  color: ${props => props.theme};
  flex-shrink: 0;
  margin-right: ${spacing.large};
  margin-top: -2px;

  ${media.large`
    margin-right: ${spacing.xLarge};
  `};
`

const Divider = styled.div`
  position: relative;
  padding: ${spacing.medium} 0;
  text-align: center;

  ${media.medium`
    padding: ${spacing.large} 0;
  `};

  ${media.large`
    padding: ${spacing.xLarge} 0;
  `};
`

const MobileDividerLabel = styled.span`
  position: static;

  ${media.medium`
    opacity: 0;
    visibility: hidden;
  `};
`

const DesktopDividerLabel = styled(H4)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate3d(-50%, -50%, 0);
  white-space: nowrap;
  font-weight: ${fontWeights.regular};
  color: ${white};

  ${mediaDown.medium`
    display: none;
  `};
`

const DividerLine = styled.div`
  position: static;
  display: block;
  width: 48px;
  height: 1px;
  background-color: ${white};
  margin-bottom: ${spacing.large};
  margin-left: auto;
  margin-right: auto;

  ${media.medium`
    display: none;
  `};
`

const Tier = ({ name, theme, benefits, requirements, hasDesktopDivider, hasDividerLine }) => (
  <>
    <Container>
      {hasDividerLine && <DividerLine />}
      <Inner>
        <Name theme={theme}>{name}</Name>
        <Content>
          {benefits.map((benefit, i) => (
            <Benefit key={i}>
              <IconWrap theme={theme}>
                <Icon icon={<Checkmark />} />
              </IconWrap>
              <span dangerouslySetInnerHTML={{ __html: benefit }} />
              {benefit === 'Birthday gift' && (
                <span>
                  &nbsp;
                  <Tooltip
                    content={
                      <>
                        <p>
                          All Vuse+ members receive a unique birthday coupon code on the 1st of their birthday calendar
                          month (e.g. if your birthday falls on July 12th, you’ll receive your reward on July 1st).
                          Birthday coupon codes will appear in the rewards tab on your vuse.com account page.
                        </p>
                        <p>
                          To be eligible to receive a birthday reward, you must have active Vuse+ member status on the
                          1st of your birthday month.
                        </p>
                        <p>
                          Birthday coupon codes are for one time use and can be applied at checkout on any vuse.com
                          order. Your birthday coupon code will be valid for 30 days from its date of issue.
                        </p>
                      </>
                    }
                  >
                    (i)
                  </Tooltip>
                </span>
              )}
            </Benefit>
          ))}
        </Content>
      </Inner>
      <Divider>
        <MobileDividerLabel>Eligibility requirements</MobileDividerLabel>
        {hasDesktopDivider && <DesktopDividerLabel>Eligibility requirements</DesktopDividerLabel>}
      </Divider>
      <Inner>
        <Content>
          {requirements.map((requirement, i) => (
            <Benefit key={i}>
              <IconWrap>
                <Icon icon={requirement.icon} />
              </IconWrap>
              <span>{requirement.text}</span>
            </Benefit>
          ))}
        </Content>
      </Inner>
    </Container>
  </>
)

Tier.propTypes = {
  name: string.isRequired,
  theme: string.isRequired,
  benefits: array.isRequired,
  requirements: array.isRequired,
  hasDesktopDivider: bool,
  hasDividerLine: bool
}

export default Tier
